import type { AdminCampaignDto } from "Infrastructure/Api/Api";
import { Resources, type TFunction } from "Translations/Resources";
import type { ChipProps } from "@mui/material";
import {
  calculateCampaignStatus,
  getDaysLeftUntilDate,
  type CampaignStatus,
} from "Utils/Campaign/campaignStatusUtils";
import { getDaysUntilDate } from "Utils/DateUtils";

const RESOURCES = Resources.Admin.Campaigns.Status;

export type { CampaignStatus };

export type CampaignStatusInfo = {
  status: CampaignStatus;
  label: string;
  color: ChipProps["color"];
  description: string;
};

export const getCampaignStatus = (
  campaign: AdminCampaignDto,
  t: TFunction,
): CampaignStatusInfo => {
  const status = calculateCampaignStatus(campaign);

  if (status === "draft") {
    return {
      status: "draft",
      label: t(RESOURCES.Draft),
      color: "info",
      description: t(RESOURCES.DraftDescription),
    };
  }

  if (status === "archived") {
    return {
      status: "archived",
      label: t(RESOURCES.Archived),
      color: "default",
      description: t(RESOURCES.ArchivedDescription),
    };
  }

  if (status === "unlimited") {
    return {
      status: "unlimited",
      label: t(RESOURCES.ActiveUnlimited),
      color: "success",
      description: t(RESOURCES.UnlimitedDescription),
    };
  }

  const startDate = campaign.startDate ? new Date(campaign.startDate) : null;
  const endDate = campaign.endDate ? new Date(campaign.endDate) : null;

  if (status === "not-started" && startDate) {
    const daysUntilStart = getDaysUntilDate(startDate);
    return {
      status: "not-started",
      label:
        daysUntilStart === 0
          ? t(RESOURCES.StartsToday)
          : daysUntilStart === 1
            ? t(RESOURCES.StartsTomorrow)
            : t(RESOURCES.StartsInDays, { days: daysUntilStart }),
      color: "warning",
      description: t(RESOURCES.StartsOn, {
        date: startDate.toLocaleDateString(),
      }),
    };
  }

  if (status === "finished" && endDate) {
    return {
      status: "finished",
      label: t(RESOURCES.Finished),
      color: "error",
      description: t(RESOURCES.EndedOn, { date: endDate.toLocaleDateString() }),
    };
  }

  if (status === "active") {
    if (endDate) {
      const daysLeft = getDaysLeftUntilDate(endDate);
      return {
        status: "active",
        label:
          daysLeft === 0
            ? t(RESOURCES.EndsToday)
            : daysLeft === 1
              ? t(RESOURCES.DayLeft)
              : t(RESOURCES.DaysLeft, { days: daysLeft }),
        color: "success",
        description: t(RESOURCES.EndsOn, {
          date: endDate.toLocaleDateString(),
        }),
      };
    }

    return {
      status: "active",
      label: t(RESOURCES.Active),
      color: "success",
      description: t(RESOURCES.CurrentlyRunning),
    };
  }

  return {
    status: "active",
    label: t(RESOURCES.Active),
    color: "success",
    description: t(RESOURCES.CurrentlyRunning),
  };
};
